import React, { useState } from 'react';
import { Plus, Minus, AlertTriangle, Star } from 'lucide-react';
import { products, categories } from '../data/products';

const ProductCard = ({ product, qty, onAdd, onRemove }) => (
  <div
    data-testid={`product-card-${product.id}`}
    className="bg-white border border-[#EBE5DB] hover:border-[#D4A853]/60 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 flex flex-col relative"
  >
    {/* Featured Badge */}
    {product.featured && (
      <div className="absolute top-3 left-3 z-10 bg-[#D4A853] text-white text-xs font-bold uppercase tracking-wider px-2 py-1 flex items-center gap-1">
        <Star size={10} fill="white" />
        La Joya
      </div>
    )}

    {/* Image */}
    <div className="aspect-square overflow-hidden">
      <img
        src={product.image}
        alt={product.name}
        loading="lazy"
        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
      />
    </div>

    {/* Info */}
    <div className="p-4 flex flex-col flex-1">
      <div className="flex items-start justify-between gap-2 mb-1">
        <h3 className="font-serif text-sm font-bold text-[#2C2C2A] leading-tight flex-1">
          {product.name}
        </h3>
        <span className="text-[#7B1F3A] font-bold text-sm whitespace-nowrap">
          {product.price.toFixed(2)}€/u
        </span>
      </div>

      <p className="text-xs text-[#8A8A87] mb-3 leading-relaxed flex-1">
        {product.description}
      </p>

      {product.warning && (
        <div className="flex items-center gap-1 text-amber-600 text-xs mb-2">
          <AlertTriangle size={11} />
          <span>{product.warning}</span>
        </div>
      )}

      <div className="flex flex-wrap gap-1 mb-4">
        {product.allergens.map((a) => (
        <span key={a} className="text-xs bg-[#EDE7D8] text-[#888780] px-2 py-0.5">
            {a}
          </span>
        ))}
      </div>

      {/* Cart Controls */}
      {qty === 0 ? (
        <button
          data-testid={`add-to-cart-${product.id}`}
          onClick={() => onAdd(product)}
          className="w-full bg-[#7B1F3A] text-white py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-[#5A1529] transition-colors duration-200 rounded-none mt-auto"
        >
          Añadir al pedido
        </button>
      ) : (
        <div className="flex items-center justify-between border border-[#2C2C2A] mt-auto">
          <button
            data-testid={`remove-from-cart-${product.id}`}
            onClick={() => onRemove(product.id)}
            className="p-2.5 hover:bg-[#F5F0E8] transition-colors"
          >
            <Minus size={14} />
          </button>
          <span
            data-testid={`cart-qty-${product.id}`}
            className="font-bold text-sm px-4 text-[#2C2C2A]"
          >
            {qty}
          </span>
          <button
            data-testid={`add-more-cart-${product.id}`}
            onClick={() => onAdd(product)}
            className="p-2.5 hover:bg-[#F5F0E8] transition-colors"
          >
            <Plus size={14} />
          </button>
        </div>
      )}
    </div>
  </div>
);

const CatalogoSection = ({ cart, onAdd, onRemove }) => {
  const [activeCategory, setActiveCategory] = useState('Todos');

  const filtered =
    activeCategory === 'Todos'
      ? products
      : products.filter((p) => p.category === activeCategory);

  return (
    <section
      id="catalogo"
      data-testid="catalogo-section"
      className="py-20 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#7B1F3A] mb-3">
            Nuestras Croquetas
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl font-bold text-[#2C2C2A]">
            El Catálogo
          </h2>
          <p className="text-[#888780] mt-4 max-w-xl mx-auto text-base">
            14 variedades 100% caseras. Todas hechas a mano con ingredientes
            de primera calidad. Precio por unidad.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 justify-center mb-12">
          {categories.map((cat) => (
            <button
              key={cat}
              data-testid={`filter-${cat.toLowerCase()}`}
              onClick={() => setActiveCategory(cat)}
              className={`px-6 py-2 text-xs font-bold uppercase tracking-widest rounded-none transition-all duration-200 ${
                activeCategory === cat
                  ? 'bg-[#7B1F3A] text-white'
                  : 'border border-[#2C2C2A] text-[#2C2C2A] hover:bg-[#2C2C2A] hover:text-[#F5F0E8]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filtered.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              qty={cart[product.id]?.quantity || 0}
              onAdd={onAdd}
              onRemove={onRemove}
            />
          ))}
        </div>

        {/* Category Count */}
        <p className="text-center text-xs text-[#8A8A87] mt-8 tracking-wide">
          Mostrando {filtered.length} de {products.length} variedades
        </p>
      </div>
    </section>
  );
};

export default CatalogoSection;
